import React from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../auth";
import { ClipboardList, PackageOpen, LogOut, ArrowRight, Palette } from "lucide-react";

const LOGO_HORIZONTAL = "https://customer-assets.emergentagent.com/job_brand-analytics-10/artifacts/1ogynudb_logo_horizontal_HT.png";

export default function Modules() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const canTrade = user?.role === "admin" || user?.can_trade === true;
  const canBandeo = user?.role === "admin" || user?.can_bandeo === true;
  const canMaquillaje = user?.role === "admin" || user?.can_maquillaje === true;

  const onLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col" data-testid="modules-page">
      <header className="border-b border-slate-200 bg-white">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <img src={LOGO_HORIZONTAL} alt="Tzanetatos" className="h-9 object-contain" />
          <button
            onClick={onLogout}
            className="text-sm text-slate-600 hover:text-slate-900 flex items-center gap-2"
            data-testid="modules-logout-btn"
          >
            <LogOut className="w-4 h-4" /> Salir
          </button>
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-5xl">
          <div className="text-center mb-12">
            <div className="kbd-label text-emerald-700 mb-3">/ Hola, {user?.name?.split(" ")[0]}</div>
            <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight text-slate-950">
              ¿A qué módulo quieres entrar?
            </h1>
            <p className="text-slate-500 mt-3">Selecciona el módulo para continuar.</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            <ModuleCard
              testId="module-trade"
              icon={ClipboardList}
              kicker="Reportes de visita"
              title="TRADE"
              description="Reportes de visita en punto de venta: disponibilidad, visibilidad, precios, competencia y más."
              onClick={() => canTrade && navigate("/dashboard")}
              available={canTrade}
              lockedMessage="Solicita acceso al administrador"
            />
            <ModuleCard
              testId="module-bandeo"
              icon={PackageOpen}
              kicker="Atado de productos"
              title="BANDEO"
              description="Registro de bandeos en cadena: entregas, productos atados, horarios y fotos."
              onClick={() => canBandeo && navigate("/bandeos")}
              available={canBandeo}
              lockedMessage="Solicita acceso al administrador"
            />
            <ModuleCard
              testId="module-maquillaje"
              icon={Palette}
              kicker="Diagnóstico de clienta"
              title="MAQUILLAJE"
              description="Ficha de diagnóstico de maquillaje: piel, base, ojos, labios, recomendaciones y seguimiento."
              onClick={() => canMaquillaje && navigate("/maquillajes")}
              available={canMaquillaje}
              lockedMessage="Solicita acceso al administrador"
            />
          </div>
        </div>
      </main>
    </div>
  );
}

function ModuleCard({ testId, icon: Icon, kicker, title, description, onClick, available, lockedMessage }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!available}
      data-testid={testId}
      className={`group relative text-left rounded-xl border p-7 transition-all overflow-hidden ${
        available
          ? "bg-white border-slate-200 hover:border-emerald-600 hover:shadow-lg hover:-translate-y-0.5 cursor-pointer"
          : "bg-slate-50 border-slate-200 opacity-70 cursor-not-allowed"
      }`}
    >
      <div className="flex items-start justify-between mb-5">
        <div className={`w-14 h-14 rounded-lg flex items-center justify-center ${available ? "bg-emerald-700 text-white" : "bg-slate-300 text-slate-600"}`}>
          <Icon className="w-7 h-7" />
        </div>
        {available && (
          <ArrowRight className="w-5 h-5 text-slate-400 group-hover:text-emerald-700 group-hover:translate-x-1 transition-all" />
        )}
      </div>
      <div className="kbd-label text-emerald-700">{kicker}</div>
      <h3 className="font-display text-3xl font-bold tracking-tight text-slate-950 mt-1 mb-3">{title}</h3>
      <p className="text-sm text-slate-600 leading-relaxed">{description}</p>
      {!available && (
        <div className="mt-4 inline-flex items-center gap-2 text-xs text-slate-500 bg-white border border-slate-200 rounded-md px-2 py-1">
          🔒 {lockedMessage}
        </div>
      )}
    </button>
  );
}
